
/**
 * BoCapital API Client — TypeScript port of api.js
 * Typed, retry-capable REST + WebSocket client.
 */

import { authService } from './auth';
import { DEFAULT_API_BASE, isMockDataEnabled, resolveApiBase } from '../config/runtime';
import { attachDoctrineV4 } from '../utils/v4';
import type { V4Fields } from '../utils/v4';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface ApiConfig {
  baseUrl: string;
  wsUrl: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

/**
 * Book-wide totals converted server-side into one display currency
 * (`/api/mt/autotrader/overview` → `totals`, engines/fx_convert.py).
 *
 * The `*_complete` flags are load-bearing: false means at least one account
 * had no FX rate and is MISSING from the figure, so the consumer must show
 * that rather than round it off into a total it isn't.
 */
export interface BookTotalsPayload {
  currency: string | null;
  equity: number | null;
  balance: number | null;
  daily_pnl: number | null;
  equity_complete: boolean;
  balance_complete: boolean;
  daily_pnl_complete: boolean;
  rates?: Record<string, number>;
  by_currency?: Record<string, number>;
  accounts_counted?: number;
}

export interface RiskRegime {
  regime: 'RISK_ON' | 'RISK_OFF' | 'MIXED' | 'NEUTRAL';
  confidence: number;
  signals: Record<string, any>;
  recommendation: string;
  risk_level: string;
  transition_probability: number;
}

export interface SessionState {
  session: 'ASIAN' | 'EUROPEAN' | 'US' | 'ASIAN_NIGHT';
  intraday_phase: 'ASIAN_RANGE' | 'LONDON_TRAP' | 'LONDON_MOVE' | 'NYC_REVERSAL' | 'NYC_CONTINUATION' | 'OFF_SESSION';
  hour_gmt: number;
  /** optional human-readable NYC bias message pushed from server */
  nyc_bias?: string;
}

/* Under CYCLE_VIEW_SOURCE=v4 (prod) /api/bocapital/state serves the doctrine
   V4 in level / direction / cycle_day / phase plus the flat v4_* fields
   (utils/v4.ts); the state engine's cycle_phase / is_reset / reset_confidence
   are no longer in the reader fields (kept as state_engine_*). With no V4 read
   level / direction / move / day are null and v4_available is false. */
export interface CycleContext extends V4Fields {
  direction: 'up' | 'down' | null;
  cycle_phase?: 'ACCUMULATION' | 'MARK_UP' | 'DISTRIBUTION' | 'MARK_DOWN' | 'RESET' | 'UNKNOWN';
  cycle_day: number | null;
  move_multiple_adr: number | null;
  peak_to_peak_multiple_adr: number;
  classification: string;
  level: 1 | 2 | 3 | null;
  macro_pattern: string | null;
  is_reset?: boolean;
  reset_confidence?: number;
  phase?: 'UP' | 'DOWN' | 'RESET' | 'RESET_UP' | 'RESET_DOWN' | null;
  transition_reason: string | null;
  retail_net_position: 'LONG' | 'SHORT' | 'MIXED';
  cycle_source?: 'v4' | 'state_engine' | 'v4_unavailable' | string;
  state_engine_cycle_phase?: string;
  state_engine_is_reset?: boolean;
  state_engine_reset_confidence?: number;
}

export interface PeakContext {
  status: 'NONE' | 'UNCONFIRMED' | 'CONFIRMED';
  bias: 'LONG' | 'SHORT' | 'NEUTRAL';
  unconfirmed_score: number;
  unconfirmed_conditions: Record<string, boolean>;
  confirmed_score: number;
  confirmed_conditions: Record<string, boolean>;
  distance_from_prev_peak: number;
}

export interface Indicators {
  ema_5: number;
  ema_13: number;
  ema_50: number;
  ema_200: number;
  ema_800: number;
  ema_alignment: 'BULLISH_ALIGNED' | 'BEARISH_ALIGNED' | 'MIXED';
  adr: number;
  adr_pips: number;
  tdi_rsi: number;
  tdi_fast_ma: number;
  tdi_slow_ma: number;
  tdi_bias: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  tdi_angle_state: string;
  [key: string]: any;
}

/**
 * Currency-strength panel (2026-08-05 cross expansion).
 *
 * `coverage` and `agreement` are as load-bearing as `score`: a decisive-looking
 * score from a half-covered or split panel is exactly what the verdict field
 * exists to suppress, so the UI must never render score alone.
 */
export interface CurrencyPanelEntry {
  currency: string;
  score: number;              // [-1, +1], + = the currency is being bought
  agreement: number;          // [0, 1] fraction of members backing the score
  coverage: number;           // [0, 1] fraction of the 7 members usable
  confidence: number;         // agreement x coverage
  verdict: 'LONG' | 'SHORT' | 'NEUTRAL' | 'CONTESTED' | 'INSUFFICIENT';
  reason: string;
  members: { pair: string; contribution: number; raw_score: number;
             sign: number; tier: 'trade' | 'observe' }[];
  dropped: { pair: string; reason: string }[];
}

export interface CurrencyPanelResponse {
  features: {
    observe_enabled: boolean;
    panel_enabled: boolean;
    panel_resolution_enabled: boolean;
    thesis_weight: number;
  };
  generated_at?: string | null;
  panels: Record<string, CurrencyPanelEntry>;
  observations?: any[];
  reason?: string;
  error?: string;
}

export interface CurrencyExpression {
  pair: string;
  side: 'LONG' | 'SHORT';
  currency: string;
  tradeable: boolean;         // false = observe-only cross; entry gate refuses it
  score: number;
  has_pattern: boolean;
  reason: string;
  components: Record<string, any>;
}

export interface CurrencyExpressionsResponse {
  features: CurrencyPanelResponse['features'];
  generated_at?: string;
  expressions: CurrencyExpression[];
  reason?: string;
  error?: string;
}

export interface BOCState {
  symbol: string;
  timestamp: string;
  current_price: number;
  indicators: Indicators;
  cycle: CycleContext;
  peak: PeakContext;
  session: SessionState;
}

export interface Setup {
  pair: string;
  setup_category: string;
  direction: 'BULLISH' | 'BEARISH';
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  cycle_level: string;
  level_count: { days_remaining: number; reversal_probability: number };
  confluence_indicators: string[];
  timestamp: string;
  outcome?: 'WIN' | 'LOSS' | 'OPEN' | string; /* injected by scanner */
  mw?: boolean;
  ar?: boolean;
  /* V4 cycle read, emitted alongside the legacy cycle_level -- never instead
     of it, so a level shown on screen can always be attributed to an engine.
     All optional: V4 reports an absent read rather than a fabricated level. */
  v4_available?: boolean;
  v4_unavailable_reason?: string | null;
  v4_direction?: 'BULLISH' | 'BEARISH' | null;
  v4_level?: number | null;
  v4_ma_level?: number | null;
  v4_level_confirmation?: 'CONFIRMED' | 'CONFLICT' | 'UNCONFIRMED' | null;
  v4_travelled_adr?: number | null;
  v4_reset_state?: string | null;
  v4_peak_kind?: 'M' | 'W' | null;
  v4_peak_price?: number | null;
  v4_agrees_with_legacy?: boolean | null;
  v4_score?: number | null;
  /* The DOCTRINE's V4 for this pair (the read that trades), attached client
     side from the /setups payload's doctrine_v4 map. Screens prefer it over
     the scanner's v4_* fields above and the legacy cycle_level. */
  doctrine_v4?: V4Fields;
  [key: string]: any;
}

// ── Scanner chart (lightweight-charts popup) ────────────────────────────────
export interface ChartBar { t: number; o: number; h: number; l: number; c: number; v?: number }
export interface ChartPoint { t: number; v: number }
export interface ChartTdiPoint {
  t: number;
  rsi: number | null;
  price: number | null;    // fast MA (green)
  signal: number | null;   // slow MA (red)
  baseline: number | null; // band mid
  upper: number | null;
  lower: number | null;
}
// One painted Asian-range box (21:00–06:00 UTC session high→low) for the chart.
export interface ChartAsianBox { start_t: number; end_t: number; high: number; low: number }
export interface SetupChartResponse {
  status: 'success' | 'no_data' | 'error';
  pair: string;
  interval: string;
  source: string | null;
  bars: ChartBar[];
  emas: Record<string, ChartPoint[]>;
  tdi: ChartTdiPoint[];
  levels: Partial<Record<
    'ar50' | 'asian_high' | 'asian_low' | 'adr_high' | 'adr_low' | 'l1' | 'l2' | 'l3',
    number
  >> & {
    asian_boxes?: ChartAsianBox[];
    /** ADR in pips (mean of the last 20 full days, day = 21:00 UTC) and the pip size. */
    adr_pips?: number;
    pip?: number;
    /** Prior trading day's high / low. */
    pdh?: number;
    pdl?: number;
    /** Start of the trading day the levels belong to (UTC seconds). */
    day_start?: number;
  };
  /** Live-candle contract (api/chart_live_bars.py ↔ SetupChart/liveBars.ts). Absent on older builds. */
  interval_s?: number | null;
  /** 'utc' = bars open on UTC multiples; 'ny_close' = 4h/1D bars follow the 17:00 New York FX day. */
  bar_anchor?: 'utc' | 'ny_close';
  /** Price the live ticks are drawn at. */
  price_basis?: 'mid';
  /** True for the trailing (live) window; false for historical pins and scroll-back pages. */
  live?: boolean;
  /** What the server's forming-bar rebuild from 1m did, and why it didn't when it didn't. */
  forming_bar?: { patched: number; added: number; reason: string | null } | null;
  /** Server clock (UTC seconds) when the response was built — the chart's tick-age clock. */
  server_time?: number;
}

export interface PatternRecord {
  id: string;
  source?: string;
  source_id?: number;
  pair: string;
  pattern: 'MW' | 'HB' | 'RRT' | 'NYC_REVERSAL' | 'STOP_HUNT' | 'BOCAPITAL' | string;
  pattern_label?: string;
  setup_type?: string | null;
  ar_level?: string | null;
  /** Derived formation group for the Pattern badge + filter: M|W|HB|RRT|STOP_HUNT|NYC_REVERSAL|'' */
  formation_group?: string;
  /** Human label for the formation badge (e.g. "M", "W", "HB", "RRT", "Stop Hunt"). */
  formation_label?: string;
  /** System setup code (AR-level + formation + direction), e.g. "AR50MS". Not a badge. */
  setup_code?: string;
  confidence_label: 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_unified: number;
  direction: 'LONG' | 'SHORT';
  session: 'ASIAN' | 'LONDON' | 'NYC' | string;
  cycle_day: number;
  cycle_direction: 'UP' | 'DOWN' | string;
  trap_pct?: number | null;
  trap_direction?: 'LONG' | 'SHORT' | string;
  outcome: 'WIN' | 'LOSS' | 'BE' | 'OPEN' | string;
  pnl_r?: number | null;
  pnl_money?: number | null;
  timestamp: string;
  chart_url?: string;
  chart_snapshot?: string;
  entry_price?: number;
  stop_loss?: number;
  initial_target?: number;
  risk_reward_ratio?: number;
  cycle_level?: number | string;
  tradeable?: boolean;
  reviewed?: boolean;
  reviewed_at?: string;
  /** True detection time (P0a) — the moment the pattern was first detected, NOT the
   *  journal write time. Prefer this over `timestamp` for display. ISO-8601 UTC. */
  detected_at?: string;
  /** True when the outcome was simulated via candle replay (no real broker fill).
   *  Drives the REAL vs SIMULATED badge so a simulated WIN is never shown as a fill. */
  is_simulated?: boolean;
  /** Linked execution ticket id when this setup was actually taken; null/absent if not. */
  trade_id?: string | null;
  /** Real broker fill price when taken; null/absent for simulated/untaken setups. */
  actual_fill_price?: number | null;
  /** Session in which the trade actually executed, if different from the detection session. */
  execution_session?: string | null;
  study_tags?: string[];
  /** Frozen 12-char detection mint — the cross-store key that joins to
   *  setup_decisions.jsonl (engine_commit/cycle_dir_confidence). Exposed
   *  top-level so it survives the lite Journal fetch. */
  setup_id?: string | null;
  setup_signature?: string | null;
  learning_context?: Record<string, any>;
  setup_snapshot?: Record<string, any>;
  raw?: Record<string, any>;
  [key: string]: any;
}

/** Operator classification shared by taken + missed precision reviews. */
export interface PrecisionClassification {
  valid?: string;
  grade?: string;
  location?: string;
  pattern_ok?: string;
  direction_ok?: string;
  timing?: string;
  real_setup_type?: string;
  real_setup_time?: string;
  went_down_level?: string;
  sl_ok?: string;
  reason_code?: string;
  notes?: string;
}

export interface TradeReviewPayload extends PrecisionClassification {
  record_type?: 'taken';
  ticket_id?: string | null;
  setup_id?: string | null;
  setup_signature?: string | null;
  trace_id?: string | null;
  pattern_id?: string | null;
  account_id?: string | null;
  pair?: string;
  direction?: string;
  trading_day?: string;
  session?: string;
  // read-only context the form displayed (copied verbatim)
  category?: string;
  setup_type?: string;
  pattern?: string;
  cycle_level?: number | string | null;
  entry?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  rr?: number | null;
  confidence?: number | null;
  confluence?: string;
  quality_score?: number | null;
  outcome?: string;
  time_utc?: string;
}

export interface MissedTradePayload extends PrecisionClassification {
  record_type?: 'missed';
  source_stage: string;
  setup_id?: string | null;
  setup_fingerprint?: string | null;
  pattern_id?: string | null;
  account_id?: string | null;
  pair: string;
  direction?: string;
  trading_day?: string;
  session?: string;
  why_missed?: string;
  why_it_was_good?: string;
  primary_veto?: string | null;
  veto_codes?: string[];
  reasons?: string[];
  counterfactual_r?: number | null;
  would_have_been_win?: boolean | null;
  peak_favorable_r?: number | null;
  category?: string;
  cycle_level?: number | string | null;
  entry?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  time_utc?: string;
}

export interface MissedCandidate {
  candidate_kind: string;
  source_stage: string;
  setup_id?: string | null;
  setup_fingerprint?: string | null;
  pattern_id?: string | null;
  pair: string;
  direction?: string;
  session?: string;
  trading_day?: string;
  as_of?: string;
  category?: string;
  setup_type?: string;
  pattern?: string;
  cycle_level?: number | string | null;
  entry?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  rr?: number | null;
  confidence?: number | null;
  quality_score?: number | null;
  outcome?: string;
  primary_veto?: string | null;
  why_missed?: string;
  veto_codes?: string[];
  reasons?: string[];
  counterfactual_r?: number | null;
  would_have_been_win?: boolean | null;
  peak_favorable_r?: number | null;
  cf_status?: string;
}

export type PrecisionRecord = Record<string, any>;

export interface MissedCandidatesResponse {
  status: string;
  source: string;
  count: number;
  candidates: MissedCandidate[];
}

export interface PrecisionReviewsResponse {
  status: string;
  reviews: PrecisionRecord[];
  missed: PrecisionRecord[];
  reviewed_ticket_ids: string[];
  count: number;
}

export interface PatternHistoryResponse {
  status: string;
  patterns: PatternRecord[];
  count: number;
  total: number;
  limit: number;
  offset: number;
  next_offset?: number | null;
  timestamp?: string;
}

export interface PatternStatsResponse {
  status: string;
  generated_at: string;
  total_patterns: number;
  win_rate: number;
  avg_r_multiple: number;
  best_pattern?: string | null;
  best_pattern_win_rate?: number;
  new_today: number;
  reviewed: number;
  unreviewed: number;
  by_pattern: Record<string, {
    total: number;
    closed: number;
    wins: number;
    win_rate: number;
    avg_r: number;
  }>;
}

export interface PatternsAnalyticsGroupBucket {
  total: number;
  closed: number;
  wins: number;
  win_rate: number;
  avg_r: number;
}

export interface PatternsAnalyticsResponse {
  status: string;
  generated_at: string;
  total_trades: number;
  closed_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  profit_factor: number;
  avg_r: number;
  expectancy: number;
  net_pnl_money?: number;
  avg_pnl_money?: number;
  by_pair: Record<string, PatternsAnalyticsGroupBucket>;
  by_session: Record<string, PatternsAnalyticsGroupBucket>;
  by_strategy: Record<string, PatternsAnalyticsGroupBucket>;
  by_day_of_week: Record<string, PatternsAnalyticsGroupBucket>;
  equity_curve: Array<{ timestamp: string; r_multiple: number; cumulative_r: number; pnl_money?: number; cumulative_money?: number }>;
}

export interface AutotraderAccountInfo {
  id: string;
  label: string;
  enabled: boolean;
  broker?: string;
}

/** Provisioning job lifecycle for new MT5/FIX accounts. */
export type ProvisionJobStatus =
  | 'pending'
  | 'provisioning'
  | 'login_pending'
  | 'active'
  | 'failed';

export interface ProvisionJob {
  job_id: string;
  account_id: string;
  display_name: string;
  broker: string;
  account_type: 'mt5' | 'fix' | string;
  status: ProvisionJobStatus;
  vm_name?: string | null;
  vnc_password?: string | null;
  vnc_command?: string | null;
  external_ip?: string | null;
  error?: string | null;
  log_tail?: string;
  created_at: string;
  updated_at: string;
}

/** Broker-quality scorecard types — Beat-Scummy-Brokers, Slice 4. */
export interface BrokerQualityAccountSummary {
  account_id: string;
  display_name: string;
  broker: string;
  spread_pairs_tracked: number;
  fill_pairs_tracked: number;
}

export interface BrokerQualityLiveQuote {
  bid?: number;
  ask?: number;
  mid?: number;
  spread_pips?: number;
  spread_points?: number;
  digits?: number;
  age_s?: number;
}

export interface BrokerQualitySpread {
  current_pips: number;
  baseline_pips: number;
  n_samples: number;
  anomaly_count: number;
  last_anomaly_ts: number;
}

export interface BrokerQualityScorecard {
  n_fills?: number;
  mean_slip_pips?: number;
  median_slip_pips?: number;
  negative_slip_rate?: number;
  worst_slip_pips?: number;
  mean_spread_at_fill_pips?: number | null;
  stop_hunts_total?: number;
  toxic?: boolean;
}

export interface BrokerQualityReference {
  mid?: number | null;
  source?: string | null;
  age_s?: number | null;
  divergence_pips_vs_broker?: number | null;
}

export interface BrokerQualityPair {
  symbol: string;
  live: BrokerQualityLiveQuote;
  spread: BrokerQualitySpread;
  scorecard: BrokerQualityScorecard;
  reference: BrokerQualityReference;
}

export interface BrokerQualityForAccount {
  account_id: string;
  pairs: BrokerQualityPair[];
}

export interface EdgeDecayArchetypeStat {
  all_time_total: number;
  all_time_wins: number;
  all_time_wr: number;
  rolling_total: number;
  rolling_wins: number;
  rolling_wr: number;
  divergence: number;
  status: 'DECAYING' | 'EMERGING' | 'STABLE' | 'INSUFFICIENT_DATA' | string;
  multiplier: number;
  is_location: boolean;
}

export interface EdgeDecayAlert {
  archetype: string;
  status: 'DECAYING' | 'EMERGING' | string;
  all_time_wr: number;
  rolling_wr: number;
  divergence: number;
  multiplier: number;
  message: string;
}

export interface EdgeDecayStatusResponse {
  status: string;
  generated_at: string;
  total_records: number;
  archetypes: Record<string, EdgeDecayArchetypeStat>;
  alerts: EdgeDecayAlert[];
  config?: {
    rolling_window?: number;
    min_all_time_trades?: number;
    wr_divergence_threshold?: number;
    emergence_threshold?: number;
  };
}

export interface PatternChartResponse {
  status: string;
  pattern_id: string;
  chart_snapshot: string;
  svg?: string;
}

export interface PeaksAllResponse {
  status: string;
  timestamp: string;
  count: number;
  pairs: Record<string, {
    peak_state: Record<string, any> | null;
    peak_override: string | null;
  }>;
}

// ── Claude Advisor (advisory only) ──────────────────────────────────────────

export interface ClaudeAdvisorStatus {
  advisory_banner: string;
  enabled: boolean;
  daily_review_enabled: boolean;
  post_mortem_enabled: boolean;
  config_tuning_enabled: boolean;
  market_take_enabled: boolean;
  market_take_interval_seconds?: number;
  market_take_model?: string;
  market_take_provider?: 'claude' | 'openai';
  openai_fallback_enabled?: boolean;
  openai_fallback_jobs?: string;
  claude_spend_limit_reset_at?: string | null;
  daily_brief_last_generated_at: string | null;
  proposals_last_generated_at: string | null;
  market_take_last_generated_at: string | null;
}

export interface ClaudeObservation {
  severity: 'info' | 'warn' | 'red';
  topic: string;
  evidence: string;
  suggested_action: string | null;
}

export interface ClaudeDailyBrief {
  advisory_banner: string;
  generated_at: string;
  window_hours: number;
  records_considered: number;
  duration_ms: number | null;
  prompt_hash: string | null;
  provider?: 'claude' | 'openai';
  model?: string | null;
  fallback_from?: 'claude' | null;
  fallback_reason?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  result: {
    summary: string;
    observations: ClaudeObservation[];
    regime_read: string;
  };
}

export interface ClaudeMarketTakeRefreshStatus {
  advisory_banner: string;
  accepted?: boolean;
  in_progress: boolean;
  started_at: string | null;
  completed_at: string | null;
  last_ok: boolean | null;
  reason: string | null;
  detail: string | null;
  provider: 'claude' | 'openai' | null;
  model: string | null;
}

export type ClaudeSeverity = 'info' | 'warn' | 'red';

export interface ClaudeMarketTakeDissent {
  topic: string;
  jarvis_says: string;
  claude_says: string;
  severity: ClaudeSeverity;
}

export interface ClaudeMarketTakeHeadline {
  text: string;
  severity: ClaudeSeverity;
}

export interface ClaudeMarketTake {
  advisory_banner: string;
  generated_at: string;
  window_minutes: number;
  pipeline_records_considered: number;
  duration_ms: number | null;
  prompt_hash: string | null;
  provider?: 'claude' | 'openai';
  model?: string | null;
  fallback_from?: 'claude' | null;
  fallback_reason?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  result: {
    summary: string;
    regime_read: string;
    dissent: ClaudeMarketTakeDissent[];
    headlines: ClaudeMarketTakeHeadline[];
    chat_seed: string;
  };
}

export interface ClaudeProposal {
  param: string;
  current: number | string;
  proposed: number | string;
  rationale: string;
  expected_impact: string;
  evidence_setup_ids: string[];
}

export interface ClaudeProposalsSnapshot {
  advisory_banner: string;
  generated_at: string;
  window_days: number;
  duration_ms: number | null;
  prompt_hash: string | null;
  tunable_param_count: number;
  setup_id_universe_size: number;
  result: { proposals: ClaudeProposal[] };
}

export interface ClaudePostMortemIndexEntry {
  trade_id: string;
  generated_at: string | null;
}

export interface ClaudePostMortem {
  advisory_banner: string;
  generated_at: string;
  trade_id: string;
  prompt_hash: string | null;
  duration_ms: number | null;
  result: {
    trade_id: string;
    verdict: 'well-executed' | 'preventable-loss' | 'lucky-win' | 'unclear';
    decisive_factor: string;
    narrative: string;
    rule_candidate: string | null;
  };
}

export interface ArchetypeTrendPoint {
  date: string;
  win_rate: number | null;
  sample_size: number;
}

export interface ArchetypePerformanceRow {
  archetype: string;
  total_trades: number;
  wins: number;
  losses: number;
  breakeven: number;
  win_rate: number;
  avg_r: number | null;
  avg_holding_minutes: number | null;
  avg_holding_time: string | null;
  best_session: { name: string; win_rate: number; sample_size: number };
  worst_session: { name: string; win_rate: number; sample_size: number };
  best_cycle_level: { name: string; win_rate: number; sample_size: number };
  worst_cycle_level: { name: string; win_rate: number; sample_size: number };
  trend_rolling_30d: ArchetypeTrendPoint[];
  data_coverage: {
    r_multiple_samples: number;
    holding_time_samples: number;
    cycle_level_samples: number;
  };
}

export interface ArchetypePerformanceDashboard {
  generated_at: string;
  meta: {
    lookback_days: number | null;
    trend_days: number;
    min_trades: number;
    total_records: number;
    archetypes_returned: number;
    source_counts: { journal: number; ml_feedback: number; deduped: number };
  };
  archetypes: ArchetypePerformanceRow[];
}

export interface AccountStatus {
  balance: number;
  equity?: number;
  daily_pnl?: number;
  risk_per_trade: number;
  max_drawdown: number;
  open_trades: number;
  open_positions?: number;
  [key: string]: any;
}

// ─── Counterfactual (Idea VIII) ────────────────────────────────────────────

export interface CounterfactualBreakdown {
  total: number;
  correct_blocks: number;
  wins_missed: number;
  accuracy: number;
}

export interface CounterfactualWeakGate {
  veto_code: string;
  accuracy: number;
  total: number;
}

export interface CounterfactualAuditReport {
  total_evaluated: number;
  total_scored: number;
  total_pending: number;
  correct_blocks: number;
  wins_missed: number;
  overall_gate_accuracy: number;
  verdict?: 'ADDING_ALPHA' | 'MARGINALLY_USEFUL' | 'NEEDS_REFINEMENT';
  verdict_description: string;
  minimum_for_stats: number;
  avg_counterfactual_r: number;
  weak_gates?: CounterfactualWeakGate[];
  by_veto_code?: Record<string, CounterfactualBreakdown>;
  by_archetype?: Record<string, CounterfactualBreakdown>;
  by_pair?: Record<string, CounterfactualBreakdown>;
  eval_window_hours: number;
  lookback_days: number;
  generated_at: string;
}

export interface CounterfactualRecord {
  _id: string;
  pair: string;
  direction: string;
  archetype: string;
  entry_price: number;
  stop_loss: number;
  take_profit: number;
  risk_reward_ratio: number;
  primary_veto: string;
  veto_codes: string[];
  reasons: string[];
  cycle_level: number;
  session: string;
  regime: string;
  timestamp_gated: string;
  evaluate_after: string;
  status: 'PENDING' | 'EVALUATED' | 'EVAL_FAILED';
  evaluation_timestamp?: string;
  would_have_been_win?: boolean;
  counterfactual_r?: number;
  peak_favorable_r?: number;
  max_adverse_r?: number;
  tp_hit?: boolean;
  sl_hit?: boolean;
  high_in_window?: number;
  low_in_window?: number;
  close_24h?: number;
  close_48h?: number;
}

// ─── Default Config ────────────────────────────────────────────────────────

function buildConfig(): ApiConfig {
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  const host = typeof window !== 'undefined' ? window.location.host : '';

  let apiOrigin = resolveApiBase();
  let wsUrl = `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}/ws`;

  if (typeof window !== 'undefined' && !apiOrigin) {
    apiOrigin = window.location.origin;
  }

  if (protocol === 'capacitor:' && !apiOrigin) {
    apiOrigin = DEFAULT_API_BASE;
  }

  if (protocol === 'capacitor:' && apiOrigin === DEFAULT_API_BASE) {
    wsUrl = 'wss://bocapital-dashboard-312264840660.europe-west1.run.app/ws';
  }

  try {
    const u = new URL(apiOrigin);
    wsUrl = `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}/ws`;
  } catch {
    // Keep default wsUrl if configured URL is malformed.
  }

  if (isMockDataEnabled() && typeof window !== 'undefined') {
    console.warn('[api] Mock data flag is enabled. Disable BOC_ENABLE_MOCKS in production builds.');
  }

  return {
    baseUrl: `${apiOrigin}/api`,
    wsUrl,
    timeout: 50000,  // 50s — market-summary + pair/ai-summary call Gemini + data
    retryAttempts: 3,
    retryDelay: 1000,
  };
}

// ─── Client ────────────────────────────────────────────────────────────────

class ApiClient {
  private config: ApiConfig;

  constructor(config?: Partial<ApiConfig>) {
    this.config = { ...buildConfig(), ...config };
  }

  // ── Generic fetch with retry ──────────────────────────────────────────

  async request<T = any>(
    endpoint: string,
    options: RequestInit = {},
    retries = 0,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    const token = authService.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const res = await fetch(`${this.config.baseUrl}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.status === 401) {
        authService.onUnauthorized();
        throw Object.assign(new Error('Unauthorized'), { status: 401 });
      }
      if (!res.ok) {
        let errorMessage = `API ${res.status}: ${res.statusText}`;
        try {
          const errPayload: any = await res.json();
          // `detail`/`error` may be an object ({code, message}); String() of it read
          // "[object Object]" in every toast (2026-10-14).
          const text = (v: any): string =>
            typeof v === 'string' ? v : v?.message ? String(v.message) : v ? JSON.stringify(v) : '';
          errorMessage = text(errPayload?.detail) || text(errPayload?.error) || errorMessage;
        } catch {
          // keep fallback status message when body is not JSON
        }
        throw Object.assign(new Error(errorMessage), { status: res.status });
      }
      return (await res.json()) as T;
    } catch (err: any) {
      clearTimeout(timeoutId);
      // Never retry 401s — token is invalid, retrying would cascade onUnauthorized
      if (err?.status === 401) throw err;
      if (retries < this.config.retryAttempts && err.name !== 'AbortError') {
        await new Promise((r) => setTimeout(r, this.config.retryDelay));
        return this.request<T>(endpoint, options, retries + 1);
      }
      throw err;
    }
  }

  // ── Convenience GET / POST ────────────────────────────────────────────

  get<T = any>(endpoint: string) {
    return this.request<T>(endpoint);
  }
  post<T = any>(endpoint: string, body: any, options: RequestInit = {}) {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── BOCAPITAL Core ─────────────────────────────────────────────────────────

  async getState(pair = 'EURUSD'): Promise<BOCState> {
    const res = await this.get<any>(`/bocapital/state/${pair}`);
    // Backend returns { status, symbol, state: {...}, events, cached }
    const st = res?.state ?? res;
    return { symbol: res?.symbol ?? pair, ...st };
  }
  getSession() { return this.get<{ session: string; intraday_phase: string; hour_gmt: number }>('/session/current'); }
  getCycle(pair = 'EURUSD') { return this.get(`/cycle/current?pair=${pair}`); }
  getPeaks(pair = 'EURUSD') { return this.get(`/peaks/state?pair=${pair}`); }

  // ── Scanner ───────────────────────────────────────────────────────────

  async scanSetups(params: { pairs?: string; min_confidence?: string; top_n?: number; include_status?: boolean } = {}) {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    const res = await this.get<any>(`/setups/scan?${qs}`);
    // Backend returns top_setups; normalize to setups for the frontend.
    // doctrine_v4 (2026-10-25): per-pair doctrine V4, attached to each setup.
    const setups = attachDoctrineV4(res?.setups ?? res?.top_setups ?? [], res?.doctrine_v4);
    return { ...res, setups };
  }

  /** Fetch the setups last written by the autotrader cycle (no independent scan). */
  async getLiveSetups() {
    const res = await this.get<any>('/setups/live');
    return { ...res, setups: attachDoctrineV4(res?.setups ?? [], res?.doctrine_v4) };
  }

  // helper used by manual-trade form to look for a setup on a single pair
  scanMissedSetup(pair: string) {
    return this.get(`/setups/scan?pair=${pair}`);
  }
  scanStraightaway(pairs?: string[]) { const qs = pairs ? `?pairs=${pairs.join(',')}` : ''; return this.get(`/scanner/straightaway${qs}`); }
  scanSecondLegMW(pairs?: string[]) { const qs = pairs ? `?pairs=${pairs.join(',')}` : ''; return this.get(`/scanner/second-leg-mw${qs}`); }
  scanNYCReversal(pairs?: string[]) { const qs = pairs ? `?pairs=${pairs.join(',')}` : ''; return this.get(`/scanner/nyc-reversal${qs}`); }
  scanMultiPair(pairs?: string[]) { const qs = pairs ? `?pairs=${pairs.join(',')}` : ''; return this.get(`/scanner/multi-pair${qs}`); }

  /**
   * Candles + EMA stack + TDI + reference levels for the Scanner setup chart.
   * Pass `asOf` (ISO timestamp) to pin the window to a historical setup — the
   * journal uses this to replay the setup in question instead of latest bars.
   */
  /** Candles + overlays for SetupChart. `before` (UTC seconds) pages back for scrolling left. */
  getSetupChart(pair: string, interval = '15m', bars = 250, asOf?: string | null, before?: number) {
    const qs = new URLSearchParams({ pair, interval, bars: String(bars) });
    if (asOf) qs.set('as_of', asOf);
    if (before !== undefined) qs.set('before', String(before));
    return this.get<SetupChartResponse>(`/chart/setup?${qs.toString()}`);
  }

  // ── TP Engine ──────────────────────────────────────────────────────────

  calculateTP(data: { entry_price: number; stop_loss: number; direction: string; pair: string }) { return this.post('/tp-engine/calculate', data); }

  // ── Cycle / NYC ────────────────────────────────────────────────────────

  getNYCStatus(pair = 'EURUSD') { return this.get(`/reversal/nyc?pair=${pair}`); }

  // ── Analysis ──────────────────────────────────────────────────────────

  analyzePair(pair: string) { return this.get(`/pair/analyze?pair=${pair}`); }
  getConfluence(pair: string) { return this.get(`/confluence/analysis?pair=${pair}`); }
  getDivergence(pair: string) { return this.get(`/divergence/detect?pair=${pair}`); }
  getMTF(pair: string) { return this.get(`/mtf/analyze?pair=${pair}`); }
  getVolumeTraps(pair: string) { return this.get(`/volume-traps/detect?pair=${pair}`); }
  getFeatures(pair: string) { return this.get(`/features/extract?pair=${pair}`); }
  getRetailSentiment(pair: string) { return this.get(`/retail-sentiment?pair=${pair}`); }
  getMarketSummary(opts: { force?: boolean } = {}) {
    return this.get(opts.force ? '/market-summary?refresh=1' : '/market-summary');
  }
  getAISummary(pair: string) { return this.get(`/pair/ai-summary?pair=${pair}`); }

  // ── Dashboard Extras ──────────────────────────────────────────────────

  getTopRecommendations(limit = 3) { return this.get(`/recommendations/top?limit=${limit}`); }
  getFxPerformance(period = '1D') { return this.get(`/fx-performance?period=${period}`); }
  chatMarketSummary(question: string, messages: any[]) { return this.post('/market-summary/chat', { question, messages }); }
  getPatternsActive() { return this.get('/patterns/active'); }
  getPatternHistory(params: {
    limit?: number;
    offset?: number;
    pair?: string;
    pattern?: string;
    ar_level?: string;
    outcome?: string;
    session?: string;
    confidence?: string;
    cycle_day?: string;
    cycle_direction?: string;
    date_from?: string;
    date_to?: string;
    min_trap_strength?: number;
    min_r_multiple?: number;
    min_win_rate?: number;
    search?: string;
    autotrader_only?: boolean;
    account_id?: string;
    /** Omit heavy nested blobs (learning_context/setup_snapshot/raw) from each
     *  record — for summary views (e.g. the Journal Overview) that only read
     *  top-level fields. Cuts payload + server serialization cost. */
    lite?: boolean;
  } = {}) {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    if (params.pair) qs.set('pair', params.pair);
    if (params.pattern) qs.set('pattern', params.pattern);
    if (params.ar_level) qs.set('ar_level', params.ar_level);
    if (params.outcome) qs.set('outcome', params.outcome);
    if (params.session) qs.set('session', params.session);
    if (params.confidence) qs.set('confidence', params.confidence);
    if (params.cycle_day) qs.set('cycle_day', params.cycle_day);
    if (params.cycle_direction) qs.set('cycle_direction', params.cycle_direction);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.min_trap_strength != null) qs.set('min_trap_strength', String(params.min_trap_strength));
    if (params.min_r_multiple != null) qs.set('min_r_multiple', String(params.min_r_multiple));
    if (params.min_win_rate != null) qs.set('min_win_rate', String(params.min_win_rate));
    if (params.search) qs.set('search', params.search);
    if (params.autotrader_only) qs.set('autotrader_only', 'true');
    if (params.account_id) qs.set('account_id', params.account_id);
    if (params.lite) qs.set('lite', 'true');
    return this.get<PatternHistoryResponse>(`/patterns/history${qs.toString() ? `?${qs.toString()}` : ''}`);
  }
  getPatternStats(params: number | { limit?: number; autotrader_only?: boolean; account_id?: string } = 5000) {
    const opts = typeof params === 'number' ? { limit: params } : params;
    const qs = new URLSearchParams();
    qs.set('limit', String(opts.limit ?? 5000));
    if (opts.autotrader_only) qs.set('autotrader_only', 'true');
    if (opts.account_id) qs.set('account_id', opts.account_id);
    return this.get<PatternStatsResponse>(`/patterns/stats?${qs.toString()}`);
  }
  getPatternsAnalytics(params: { limit?: number; autotrader_only?: boolean; account_id?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.autotrader_only) qs.set('autotrader_only', 'true');
    if (params.account_id) qs.set('account_id', params.account_id);
    return this.get<PatternsAnalyticsResponse>(`/patterns/analytics${qs.toString() ? `?${qs.toString()}` : ''}`);
  }
  getAutotraderAccounts() {
    return this.get<{ status: string; accounts: AutotraderAccountInfo[] }>('/autotrader/accounts');
  }
  getEdgeDecayStatus(force = false) {
    return this.get<EdgeDecayStatusResponse>(`/edge-decay/status${force ? '?force=1' : ''}`);
  }
  reviewPattern(patternId: string, body: { reviewed?: boolean; study_tags?: string[] } = {}) {
    return this.post<{ status: string; pattern: PatternRecord }>(`/patterns/review/${encodeURIComponent(patternId)}`, body);
  }

  // ── Precision review (in-journal, replaces the worksheet CSV loop) ──────
  /** Grade a trade we TOOK. */
  reviewTrade(body: TradeReviewPayload) {
    return this.post<{ status: string; record: PrecisionRecord }>('/precision/review', body);
  }
  /** Log a trade we should have taken (funnel pick or manual add). */
  createMissedTrade(body: MissedTradePayload) {
    return this.post<{ status: string; record: PrecisionRecord }>('/precision/missed', body);
  }
  /** Saved reviews + missed records (+ reviewed_ticket_ids for the badge). */
  getPrecisionReviews(params: { account_id?: string; since?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.account_id) qs.set('account_id', params.account_id);
    if (params.since) qs.set('since', params.since);
    return this.get<PrecisionReviewsResponse>(`/precision/reviews${qs.toString() ? `?${qs.toString()}` : ''}`);
  }
  /** Detected-but-untaken setups to pick a missed trade from. */
  getMissedCandidates(params: { source?: string; status?: string; pair?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.source) qs.set('source', params.source);
    if (params.status) qs.set('status', params.status);
    if (params.pair) qs.set('pair', params.pair);
    if (params.limit != null) qs.set('limit', String(params.limit));
    return this.get<MissedCandidatesResponse>(`/precision/missed-candidates${qs.toString() ? `?${qs.toString()}` : ''}`);
  }
  getPatternChart(patternId: string) {
    return this.get<PatternChartResponse>(`/patterns/${encodeURIComponent(patternId)}/chart`);
  }
  getMorningBrief() { return this.get('/morning-routine'); }

  // ── Risk & Market Data ────────────────────────────────────────────────

  getRiskRegime() { return this.get<RiskRegime>('/risk-regime/current'); }
  getSentiment(pair = 'EURUSD') { return this.get(`/sentiment/analyze?pair=${pair}`); }
  getEconomicCalendar() { return this.get('/economic-calendar'); }
  getFearGreed() { return this.get('/fear-greed'); }
  getMoneyFlow(period = '1D') { return this.get(`/money-flow?period=${period}`); }
  async getAccountStatus(accountId?: string): Promise<AccountStatus> {
    const res = await this.get<any>(`/risk/account-status${this._aq(accountId)}`);
    return (res?.account ?? res?.account_status ?? res) as AccountStatus;
  }
  calculatePositionSize(data: any) { return this.post('/risk/position-size', data); }
  validateTrade(data: any) { return this.post('/risk/validate-trade', data); }

  // ── Autotrader archetype stats ────────────────────────────────────────

  /** AutoTrader-only archetype win rate — sourced from trade tickets. */
  getAutoTraderArchetypeStats(accountId?: string) {
    return this.get<{ status: string; archetypes: Array<{ archetype: string; win_rate: number; total_trades: number }> }>(
      `/autotrader/archetype-stats${this._aq(accountId)}`,
    );
  }

  /** Per-archetype performance dashboard — WR, R-multiple, holding-time,
   *  best/worst session, best/worst cycle, 30-day rolling trend. Backed by
   *  ``engines.archetype_performance_engine``. */
  getArchetypePerformance(params: { lookback_days?: number; trend_days?: number; min_trades?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.lookback_days != null) qs.set('lookback_days', String(params.lookback_days));
    if (params.trend_days != null) qs.set('trend_days', String(params.trend_days));
    if (params.min_trades != null) qs.set('min_trades', String(params.min_trades));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.get<{ status: string } & ArchetypePerformanceDashboard>(
      `/archetype/performance${suffix}`,
    );
  }
  /** One-time manual clear of the archetype_outcomes collection after an account reset. */
  clearAutoTraderArchetypeStats(reason: string) {
    return this.post<{ status: string; deleted: number }>(
      '/autotrader/archetype-stats/clear',
      { confirm: 'true' },
      { headers: { 'X-Action-Reason': reason } },
    );
  }

  // ── Counterfactual (Idea VIII) ────────────────────────────────────────

  getCounterfactualAudit(days = 90) {
    return this.get<{ status: string; report: CounterfactualAuditReport }>(
      `/counterfactual/audit?days=${Math.min(days, 365)}`,
    );
  }
  getCounterfactualRecords(params: { limit?: number; status?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    return this.get<{ status: string; count: number; records: CounterfactualRecord[] }>(
      `/counterfactual/records${qs.toString() ? `?${qs.toString()}` : ''}`,
    );
  }
  triggerCounterfactualEval() {
    return this.post<{ status: string; report: { timestamp: string; evaluated: number; skipped: number; errors: number } }>(
      '/counterfactual/evaluate', {},
    );
  }

  // ── Account query-param helper ──────────────────────────────────────────
  /** Build `?account_id=X` suffix (or `&account_id=X` if qs already present). */
  private _aq(accountId?: string, existingQs = false): string {
    if (!accountId || accountId === 'default') return '';
    return `${existingQs ? '&' : '?'}account_id=${encodeURIComponent(accountId)}`;
  }

  // ── Account Registry (CRUD) ───────────────────────────────────────────

  getAccounts() { return this.get<{ status: string; accounts: any[] }>('/accounts'); }
  createAccount(data: {
    account_id: string;
    display_name: string;
    broker?: string;
    account_size?: number;
    enabled?: boolean;
    /** Non-sensitive FIX connection settings (host, port, sender_comp, …).
     *  Backend rejects password / secret keys — those must be set as
     *  FIX_<ACCOUNT_ID>_PASSWORD env vars on the API server. */
    fix_credentials?: Record<string, unknown>;
  }) {
    return this.post('/accounts', data);
  }
  getAccount(accountId: string) { return this.get(`/accounts/${encodeURIComponent(accountId)}`); }
  updateAccount(accountId: string, data: {
    display_name?: string;
    broker?: string;
    account_size?: number;
    enabled?: boolean;
    fix_credentials?: Record<string, unknown>;
  }) {
    return this.request(`/accounts/${encodeURIComponent(accountId)}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  deleteAccount(accountId: string) {
    return this.request(`/accounts/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
  }

  // ── Account Provisioning (spin up new MT5 VM) ─────────────────────────

  /**
   * Kick off async provisioning of a new MT5 VM + register the account in
   * the dashboard registry.  Returns a job_id.  Poll {@link getProvisionJob}
   * for status until it reaches 'active' or 'failed'.
   */
  provisionAccount(data: {
    account_id: string;
    display_name: string;
    account_type: 'mt5' | 'fix';
    broker_server: string;
    account_size?: number;
    vm_size?: 'e2-small' | 'e2-medium' | string;
    zone?: string;
    mt5_login?: string;
    mt5_password?: string;
  }) {
    return this.post<{ status: string; job: ProvisionJob }>('/accounts/provision', data);
  }

  getProvisionJob(jobId: string) {
    return this.get<{ status: string; job: ProvisionJob }>(`/accounts/jobs/${encodeURIComponent(jobId)}`);
  }

  // ── Broker Quality (Beat-Scummy-Brokers) ──────────────────────────────

  /** Top-level scorecard summary across every account. */
  getBrokerQualitySummary() {
    return this.get<{ accounts: BrokerQualityAccountSummary[] }>('/broker-quality');
  }

  /** Per-pair detail for a single account. */
  getBrokerQuality(accountId: string) {
    return this.get<BrokerQualityForAccount>(
      `/broker-quality/${encodeURIComponent(accountId)}`,
    );
  }

  // ── MT5 / Auto-Trader (multi-account) ─────────────────────────────────

  /** Overview of all accounts' autotrader status. */
  // `totals` = book-wide equity/balance/day-P&L already converted into one
  // display currency server-side (engines/fx_convert.py). Optional because an
  // older API revision doesn't send it; callers fall back to their own sum.
  getAutoTraderOverview() { return this.get<{ status: string; accounts: any[]; totals?: BookTotalsPayload }>('/mt/autotrader/overview'); }

  getMT5Status(accountId?: string) { return this.get(`/mt/status${this._aq(accountId)}`); }
  getMT5Positions(accountId?: string) { return this.get(`/mt/positions${this._aq(accountId)}`); }
  getMT5Orders(accountId?: string) { return this.get(`/mt/orders${this._aq(accountId)}`); }
  toggleAutoTrader(enabled: boolean, reason: string, accountId?: string) {
    return this.post(`/mt/autotrader/toggle${this._aq(accountId)}`, { enabled }, { headers: { 'X-Action-Reason': reason } });
  }
  toggleAutoTraderGlobal(enabled: boolean, reason: string) {
    return this.post<{ status: string; global_kill: { active: boolean; reason: string }; affected: string[] }>(
      '/mt/autotrader/global-toggle',
      { enabled, reason },
      { headers: { 'X-Action-Reason': reason } },
    );
  }
  getAutoTraderGlobalKill() {
    return this.get<{ status: string; global_kill: { active?: boolean; reason?: string; previously_running?: string[]; updated_at?: string } }>(
      '/mt/autotrader/global-toggle',
    );
  }
  getAutoTraderStatus(accountId?: string) { return this.get(`/mt/autotrader/status${this._aq(accountId)}`); }
  getAutoTraderStats(accountId?: string) { return this.get(`/autotrader/stats${this._aq(accountId)}`); }
  /** Per-pair cycle rows; under CYCLE_VIEW_SOURCE=v4 each row carries the doctrine V4 (v4_* fields). */
  getCycleLevels() { return this.get<{ status?: string; levels?: Record<string, V4Fields & { [k: string]: any }> }>('/cycle-levels'); }
  getMT5Config(accountId?: string) { return this.get(`/mt/config${this._aq(accountId)}`); }
  saveMT5Config(config: any, accountId?: string) { return this.post(`/mt/config${this._aq(accountId)}`, config); }
  closeAllPositions(reason: string, accountId?: string) {
    return this.post(`/mt/close-all${this._aq(accountId)}`, {}, { headers: { 'X-Action-Reason': reason } });
  }
  closePosition(ticket: number, accountId?: string) { return this.post(`/mt/close${this._aq(accountId)}`, { ticket }); }
  /** Weekend hold (operator 2026-10-25): setting, next Friday flatten window, open trades + exemptions. */
  getWeekendHold(accountId?: string) { return this.get(`/trade/weekend-hold${this._aq(accountId)}`); }
  /** Keep one open trade out of the Friday pre-close weekend flatten (or put it back). */
  setWeekendExempt(mt5Ticket: number, exempt: boolean, accountId?: string) {
    return this.post(`/trade/weekend-exempt${this._aq(accountId)}`, { mt5_ticket: mt5Ticket, exempt });
  }
  /** POST /api/trade/exemptions (operator 2026-10-28): one of weekend / news / rollover on one open trade. */
  setTradeExemption(mt5Ticket: number, kind: 'weekend' | 'news' | 'rollover', exempt: boolean, accountId?: string) {
    return this.post(`/trade/exemptions${this._aq(accountId)}`, { mt5_ticket: mt5Ticket, kind, exempt });
  }
  placeManualTrade(data: any, accountId?: string) { return this.post(`/mt/manual-trade${this._aq(accountId)}`, data); }

  // ── Trade Management (modify, partial close, limit, actions) ──────────

  modifyPositionSLTP(ticket: number, sl?: number, tp?: number, accountId?: string) {
    return this.post(`/mt/modify-sltp${this._aq(accountId)}`, { ticket, sl, tp });
  }
  partialClosePosition(ticket: number, volume: number, accountId?: string) {
    return this.post(`/mt/partial-close${this._aq(accountId)}`, { ticket, volume });
  }
  placeLimitOrder(data: {
    symbol: string; direction: 'BUY' | 'SELL'; volume: number;
    limit_price: number; sl?: number; tp?: number;
    expiry_s?: number; comment?: string;
  }, accountId?: string) {
    return this.post(`/mt/place-limit${this._aq(accountId)}`, data);
  }
  actionBreakeven(ticket: number, accountId?: string) {
    return this.post(`/mt/action/breakeven${this._aq(accountId)}`, { ticket });
  }
  actionChokeStop(ticket: number, accountId?: string) {
    return this.post(`/mt/action/choke-stop${this._aq(accountId)}`, { ticket });
  }
  actionTrailStop(ticket: number, method: 'fixed' | 'adr' = 'fixed', accountId?: string) {
    return this.post(`/mt/action/trail-stop${this._aq(accountId)}`, { ticket, method });
  }
  actionHalfClose(ticket: number, accountId?: string) {
    return this.post(`/mt/action/half-close${this._aq(accountId)}`, { ticket });
  }
  suggestLevels(pair: string, direction?: 'BUY' | 'SELL', accountId?: string) {
    const qs = direction ? `?pair=${pair}&direction=${direction}` : `?pair=${pair}`;
    return this.get(`/mt/suggest-levels${qs}${this._aq(accountId, true)}`);
  }
  getMT5History(days = 7, accountId?: string) { return this.get(`/mt/history?days=${days}${this._aq(accountId, true)}`); }
  /** Performance stats computed server-side directly from MT5 account + deal history. */
  /** `days` = the win-rate / profit-factor window (server echoes `window_days`). */
  getMT5Stats(accountId?: string, days = 30) { return this.get(`/mt/stats?days=${days}${this._aq(accountId, true)}`); }
  clearMT5History(reason: string, accountId?: string) {
    return this.post(`/mt/history/clear${this._aq(accountId)}`, {}, { headers: { 'X-Action-Reason': reason } });
  }
  disconnectBroker(accountId?: string) { return this.post(`/mt/disconnect${this._aq(accountId)}`, {}); }
  /** Clear all AutoTrader data after user confirms a new account was detected. */
  accountReset(reason: string, accountId?: string) {
    return this.post(`/mt/autotrader/account-reset${this._aq(accountId)}`, {}, { headers: { 'X-Action-Reason': reason } });
  }

  // ── Trade Lifecycle ───────────────────────────────────────────────────

  createTradeTicket(data: any) { return this.post('/trade/ticket', data); }
  executeTrade(data: any) { return this.post('/trade/execute', data); }
  closeTrade(data: any, accountId?: string) { const id = data?.trade_id || data?.id || ''; return this.post(`/trade/close/${id}${this._aq(accountId)}`, data); }
  getActiveTrades(accountId?: string) { return this.get(`/trade/active${this._aq(accountId)}`); }
  getTradeHistory() { return this.get('/trade/history'); }

  // ── Settings ──────────────────────────────────────────────────────────

  getSettings() { return this.get('/settings'); }
  saveSettings(data: any) { return this.post('/settings', data); }
  getAlertsConfig() { return this.get('/alerts/config'); }
  resetSettings(reason: string) {
    return this.post('/settings/reset', {}, { headers: { 'X-Action-Reason': reason } });
  }
  getSecretsStatus() { return this.get('/settings/secrets-status'); }
  getOperatorAuditActions(limit = 100, action?: string) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (action) qs.set('action', action);
    return this.get(`/admin/audit/actions?${qs.toString()}`);
  }

  // ── Misc ──────────────────────────────────────────────────────────────

  healthCheck() { return this.get('/health'); }

  // ── Currency-strength panel (2026-08-05 cross expansion) ────────────
  getCurrencyPanel() { return this.get<CurrencyPanelResponse>('/currency-panel'); }
  getCurrencyExpressions(currency?: string) {
    const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
    return this.get<CurrencyExpressionsResponse>(`/currency-panel/expressions${qs}`);
  }

  // ── Tick streaming (Phases 1–7) ─────────────────────────────────────
  getTickHealth() { return this.get<any>('/tick/health'); }
  getTickSignalsAB() { return this.get<any>('/tick/signals/ab'); }
  getTickJournalRange(symbol: string, fromTs: number, toTs: number, limit = 5000) {
    const qs = new URLSearchParams({
      symbol,
      from: String(fromTs),
      to:   String(toTs),
      limit: String(limit),
    });
    return this.get<any>(`/tick/journal/range?${qs.toString()}`);
  }
  getStats() { return this.get('/stats'); }
  getWeeklyShortlist() { return this.get('/shortlist/current'); }
  getPatternMemory() { return this.get('/patterns/memory'); }
  /** With `accountId`, gates that account's doctrine path bypasses come back as `doctrine_bypassed_reasons`. */
  getNoTradeCheck(pair: string, accountId?: string) { return this.get(`/no-trade/check?pair=${pair}${this._aq(accountId, true)}`); }

  // ── Matrix Extras / Peaks ─────────────────────────────────────────────

  getBulkState() { return this.get<{ states: Record<string, BOCState>; shortlist: string[]; count: number }>('/bocapital/state'); }
  overridePeak(pair: string, peak: 'HIGH' | 'LOW') { return this.post('/peaks/override', { pair, peak }); }
  deletePeakOverride(pair: string) { return this.request('/peaks/override', { method: 'DELETE', body: JSON.stringify({ pair }) }); }
  getAllPeaksState() { return this.get<PeaksAllResponse>('/peaks/state?all=true'); }

  // ── ML Feedback ────────────────────────────────────────────────────────

  getMLFeedbackStatus() { return this.get('/ml/feedback/status'); }
  getMLFeedbackInsights() { return this.get('/ml/feedback/insights'); }
  getMLCalibration() { return this.get<{ calibration: any }>('/ml/feedback/calibration'); }
  processMLFeedback() { return this.post('/ml/feedback/process', {}); }
  trainMLFeedback() { return this.post('/ml/feedback/train', {}); }
  getMLLeaderboard() { return this.get('/ml-feedback/leaderboard'); }
  getSystemWeights() { return this.get('/system/weights'); }
  getSystemConfidence() { return this.get('/system/confidence'); }
  getSystemLogs(limit = 200) { return this.get(`/logs?limit=${limit}`); }
  mlPredict(data: any) { return this.post('/ml/predict', data); }
  fepInference(data: any) { return this.post('/fep/inference', data); }
  getSetupQuality(pair: string) { return this.get(`/setup/quality?pair=${pair}`); }

  // ── AI Chat ────────────────────────────────────────────────────────────

  chatAISummary(pair: string, question: string, messages: any[]) { return this.post('/pair/ai-summary/chat', { pair, question, messages }); }

  // ── Claude Advisor (advisory only — never gates trades) ────────────────

  getClaudeAdvisorStatus(): Promise<ClaudeAdvisorStatus> {
    return this.get<ClaudeAdvisorStatus>('/intelligence/claude/status');
  }

  /** Returns the latest daily brief, or null when none exists yet (HTTP 404). */
  async getClaudeDailyBrief(): Promise<ClaudeDailyBrief | null> {
    try {
      return await this.get<ClaudeDailyBrief>('/intelligence/claude/daily-brief');
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  /** Returns the latest scheduled AI market-take, or null when the worker
   *  has not produced a snapshot yet (HTTP 404). Advisory-only — never used
   *  to gate trades, only overlaid on the Jarvis narrative. */
  async getClaudeMarketTake(): Promise<ClaudeMarketTake | null> {
    try {
      return await this.get<ClaudeMarketTake>('/intelligence/claude/market-take');
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  /** Queue a model-backed refresh. Start with retries exhausted because POST
   *  consumes quota; the backend also deduplicates concurrent clicks. */
  refreshClaudeMarketTake(): Promise<ClaudeMarketTakeRefreshStatus> {
    return this.request<ClaudeMarketTakeRefreshStatus>(
      '/intelligence/claude/market-take/refresh',
      { method: 'POST', body: JSON.stringify({}) },
      this.config.retryAttempts,
    );
  }

  getClaudeMarketTakeRefreshStatus(): Promise<ClaudeMarketTakeRefreshStatus> {
    return this.get<ClaudeMarketTakeRefreshStatus>(
      '/intelligence/claude/market-take/refresh-status',
    );
  }

  /** Returns the latest config-tuning proposals, or null when none exists yet. */
  async getClaudeProposals(): Promise<ClaudeProposalsSnapshot | null> {
    try {
      return await this.get<ClaudeProposalsSnapshot>('/intelligence/claude/proposals');
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  listClaudePostMortems(limit = 50): Promise<{ advisory_banner: string; post_mortems: ClaudePostMortemIndexEntry[] }> {
    return this.get(`/intelligence/claude/post-mortems?limit=${limit}`);
  }

  async getClaudePostMortem(tradeId: string): Promise<ClaudePostMortem | null> {
    try {
      return await this.get<ClaudePostMortem>(`/intelligence/claude/post-mortems/${encodeURIComponent(tradeId)}`);
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  }

  // ── User Setups ────────────────────────────────────────────────────────

  getUserSetupsLibrary() { return this.get('/user-setups/library'); }
  saveUserSetup(setup: any) { return this.post('/user-setups/library', setup); }
  deleteUserSetup(id: string) { return this.request(`/user-setups/library/${id}`, { method: 'DELETE' }); }
  seedUserSetups() { return this.post('/user-setups/library/seed', {}); }
  getMySetups() { return this.get('/user-setups/my-setups'); }
  saveMySetup(setup: any) { return this.post('/user-setups/my-setups', setup); }
  deleteMySetup(id: string) { return this.request(`/user-setups/my-setups/${id}`, { method: 'DELETE' }); }
  importMySetups(setups: any[]) { return this.post('/user-setups/my-setups/import', { setups }); }

  // ── Memory ─────────────────────────────────────────────────────────────

  getSetupMemory(limit = 100) { return this.get(`/memory/setups?limit=${limit}`); }
  getPeakMemory(limit = 100) { return this.get(`/memory/peaks?limit=${limit}`); }

  // ── Strategy Docs ──────────────────────────────────────────────────────

  getStrategyDocs() { return this.get('/strategy-docs'); }
  saveStrategyDoc(data: any) { return this.post('/strategy-docs', data); }
  deleteStrategyDoc(id: string) { return this.request(`/strategy-docs/${id}`, { method: 'DELETE' }); }

  // ── Backtest ────────────────────────────────────────────────────────────

  runBacktest(data: any) { return this.post('/backtest/run', data); }
  mlModelPredict(data: any) { return this.post('/ml-model/predict', data); }
  mlModelTrain(data: any) { return this.post('/ml-model/train', data); }
  generateRecommendations(data: any) { return this.post('/recommendations/generate', data); }

  // ── Batch Operations ──────────────────────────────────────────────────

  async getDashboardSummary(pair = 'EURUSD') {
    const [riskRegime, sentiment, session, calendar, fearGreed, account] = await Promise.allSettled([
      this.getRiskRegime(),
      this.getSentiment(pair),
      this.getSession(),
      this.getEconomicCalendar(),
      this.getFearGreed(),
      this.getAccountStatus(),
    ]);
    return {
      riskRegime: riskRegime.status === 'fulfilled' ? riskRegime.value : null,
      sentiment:  sentiment.status === 'fulfilled'  ? sentiment.value  : null,
      session:    session.status === 'fulfilled'    ? session.value    : null,
      calendar:   calendar.status === 'fulfilled'   ? calendar.value   : null,
      fearGreed:  fearGreed.status === 'fulfilled'  ? fearGreed.value  : null,
      account:    account.status === 'fulfilled'    ? account.value    : null,
      timestamp: new Date().toISOString(),
    };
  }

  async getComprehensiveAnalysis(pair: string) {
    const [state, confluence, divergence, volumeTraps, mtf, features, peaks] = await Promise.allSettled([
      this.getState(pair),
      this.getConfluence(pair),
      this.getDivergence(pair),
      this.getVolumeTraps(pair),
      this.getMTF(pair),
      this.getFeatures(pair),
      this.getPeaks(pair),
    ]);
    return {
      pair,
      state:       state.status === 'fulfilled'       ? state.value       : null,
      confluence:  confluence.status === 'fulfilled'  ? confluence.value  : null,
      divergence:  divergence.status === 'fulfilled'  ? divergence.value  : null,
      volumeTraps: volumeTraps.status === 'fulfilled' ? volumeTraps.value : null,
      mtf:         mtf.status === 'fulfilled'         ? mtf.value         : null,
      features:    features.status === 'fulfilled'    ? features.value    : null,
      peaks:       peaks.status === 'fulfilled'       ? peaks.value       : null,
      timestamp: new Date().toISOString(),
    };
  }

  // ── WebSocket ─────────────────────────────────────────────────────────

  connectWebSocket(handlers: {
    onMessage?: (data: any) => void;
    onOpen?: () => void;
    onError?: (err: Event) => void;
    onClose?: () => void;
  }): WebSocket | null {
    try {
      const ws = new WebSocket(this.config.wsUrl);
      ws.onopen = () => handlers.onOpen?.();
      ws.onmessage = (e) => {
        try { handlers.onMessage?.(JSON.parse(e.data)); } catch { /* skip */ }
      };
      ws.onerror = (e) => handlers.onError?.(e);
      ws.onclose = () => handlers.onClose?.();
      return ws;
    } catch {
      return null;
    }
  }
}

export const api = new ApiClient();
export default api;
